<template>
  <swiper
    :modules="modules"
    :slides-per-view="slidesPerView"
    :space-between="20"
    :loop="true"
    :autoplay="{ delay: 5000, disableOnInteraction: true }"
    class="py-4"
  >
    <swiper-slide 
      v-for="(product, index) in products" 
      :key="index" 
      class="group bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
      @click="redirectToProduct(product.lotId)"
    >
      <!-- Image produit -->
      <div class="relative h-48 bg-gray-50 overflow-hidden">
        <img 
          :src="product.photos[0] || defaultImage" 
          :alt="product.lotName" 
          class="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
          @error="handleImageError"
        >
        <!-- Badge promotion -->
        <div v-if="product.prix !== product.prix_reduit" class="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-lg">
          -{{ calculateDiscount(product.prix, product.prix_reduit) }}%
        </div>
      </div>

      <!-- Détails produit -->
      <div class="p-4">
        <h3 class="text-sm font-semibold text-gray-800 mb-1 group-hover:text-orange-500 transition-colors truncate">
          {{ product.lotName }}
        </h3>
        
        <!-- Prix -->
        <div class="flex items-baseline">
          <span class="text-lg font-bold text-orange-500">{{ formatPrice(product.prix_reduit) }}</span>
          <span v-if="product.prix !== product.prix_reduit" class="ml-2 text-[10px] text-gray-400 line-through">
            {{ formatPrice(product.prix) }}
          </span>
        </div>

        <!-- Évaluation -->
        <div class="flex items-center mt-2">
          <div class="flex text-orange-400 mr-2">
            <i class="fas fa-star text-xs"></i>
            <i class="fas fa-star text-xs ml-0.5"></i>
            <i class="fas fa-star text-xs ml-0.5"></i>
            <i class="fas fa-star text-xs ml-0.5"></i>
            <i class="fas fa-star-half-alt text-xs ml-0.5"></i>
          </div>
          <span class="text-xs text-gray-500">(24)</span>
        </div>
      </div>
    </swiper-slide>
  </swiper>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/autoplay'
import axios from 'axios'

const router = useRouter()
const defaultImage = '/img/default.png'
const products = ref([])
const slidesPerView = ref(4)
const modules = [Autoplay]

// Fonctions
const fetchMostSoldProducts = async () => {
  try {
    const response = await axios.get('/api/sales/most-sold-products')
    products.value = response.data
  } catch (error) {
    console.error('Failed to fetch products:', error)
  }
}

const redirectToProduct = (id) => {
  router.push(`/boutique/produits/${id}`)
}

const handleImageError = (event) => {
  event.target.src = defaultImage
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
}

const calculateDiscount = (originalPrice, discountedPrice) => {
  return Math.round((originalPrice - discountedPrice) / originalPrice * 100)
}

// Responsive slides
const updateSlidesPerView = () => {
  const width = window.innerWidth
  if (width < 640) slidesPerView.value = 1.5
  else if (width < 768) slidesPerView.value = 2.2
  else if (width < 1024) slidesPerView.value = 3
  else slidesPerView.value = 4
}

// Lifecycle hooks
onMounted(() => {
  fetchMostSoldProducts()
  updateSlidesPerView()
  window.addEventListener('resize', updateSlidesPerView)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSlidesPerView)
})
</script>

<style scoped>
/* Animation de survol pour les slides */
.swiper-slide {
  transition: transform 0.3s ease;
}

.swiper-slide:hover {
  transform: translateY(-5px);
}

/* Style personnalisé pour la pagination */
.swiper-pagination-bullet {
  background: #fff;
  opacity: 0.5;
}

.swiper-pagination-bullet-active {
  background: #f97316;
  opacity: 1;
}
</style>